import { useEffect, useState } from "react";
import { isEqual, get } from "lodash";
import { pluralize } from "@tvg/utils/generalUtils";
import { BettingInterest, RaceProgram, Results } from "@tvg/ts-types/Race";
import formatSequencial from "@tvg/formatter/numeric";
import formatCurrency from "@tvg/formatter/currency";
import {
  RunnerBIResult,
  RunnerResultProps,
  HorseSaddleInfo
} from "../components/Main/RaceResultView/raceResult/components/runnerResult/types";

export const formattedMoneyWon = (value: number): string =>
  value > 0 ? formatCurrency(value) : "";

export const computeBeatenDistance = (
  beatenDistance: number,
  beatenDistanceStatus: string,
  showLength: boolean
) => {
  if (beatenDistance < 0.5) return beatenDistanceStatus;

  return showLength
    ? `${beatenDistanceStatus} ${pluralize(beatenDistance, "length", false)}`
    : beatenDistanceStatus;
};

export const formatBeatenDistance = (
  beatenDistance: string,
  beatenDistanceStatus: string,
  showLength: boolean = false
) => {
  const beatenDistancePre = parseFloat(beatenDistance) < 0.5 ? "by a " : "by ";

  const computedBeatenDistanceAfter = computeBeatenDistance(
    parseFloat(beatenDistance),
    beatenDistanceStatus,
    showLength
  );
  return beatenDistancePre + computedBeatenDistanceAfter.toLowerCase();
};

const useCurrentRaceResultRunners = (
  currentRace: RaceProgram,
  bettingInterests: BettingInterest[],
  results?: Results,
  raceId?: string,
  isDriver: boolean = false
) => {
  const [runnerFromBIResults, setRunnerFromBIResults] =
    useState<RunnerBIResult>({ runnersBI: [], runnersScratched: [] });

  useEffect(() => {
    const runnersBI: RunnerResultProps[] = [];

    if (results?.runners && raceId && bettingInterests.length) {
      results.runners.forEach((runner) => {
        const { biNumber, runnerNumber } = runner;
        const betInterest = bettingInterests.find(
          (runnerBettingInterests) =>
            runnerBettingInterests.biNumber === biNumber
        );

        const biRunnersList = get(betInterest, "runners", []);
        const currentBIRunner = biRunnersList.find((biRunnerItem) =>
          isEqual(biRunnerItem.runnerId, runnerNumber)
        );

        if (currentBIRunner && betInterest) {
          const currentRunnerBI = betInterest.runners.find(
            (runnerBI) => +runnerBI.runnerId === runner.biNumber
          );
          runnersBI.push({
            currentRace,
            horseName: runner.runnerName,
            isWinner: runner.finishPosition === 1,
            horseSaddleInfo: {
              number: runner.runnerNumber,
              numberColor: betInterest.numberColor,
              saddleColor: betInterest.saddleColor,
              isScratched: currentRunnerBI?.scratched || false,
              silkImage: currentRunnerBI?.timeform?.silkUrlSvg
            },
            isDriver,
            jockeyName: currentBIRunner.jockey,
            trainerName: currentBIRunner.trainer,
            lengthToNextRacer:
              runner.timeform?.beatenDistance &&
              runner.timeform?.beatenDistanceStatus
                ? formatBeatenDistance(
                    runner.timeform?.beatenDistance,
                    runner.timeform?.beatenDistanceStatus,
                    true
                  )
                : undefined,
            racerComment: runner.timeform?.postRaceReport
              ? `"${runner.timeform?.postRaceReport}"`
              : undefined,
            resultPayouts: {
              win: formattedMoneyWon(runner.winPayoff),
              place: formattedMoneyWon(runner.placePayoff),
              show: formattedMoneyWon(runner.showPayoff)
            },
            position: formatSequencial(runner.finishPosition),
            showSilk: false
          });
        }
      });
    }

    const runnersScratched: HorseSaddleInfo[] = bettingInterests
      .filter(
        (betInterest) => betInterest.runners && betInterest.runners[0].scratched
      )
      .map((betInterest) => ({
        number: betInterest.runners[0].runnerId,
        numberColor: betInterest.numberColor,
        saddleColor: betInterest.saddleColor,
        isScratched: true
      }));

    setRunnerFromBIResults({ runnersBI, runnersScratched });
  }, [raceId, bettingInterests?.length, results]);

  return runnerFromBIResults;
};

export default useCurrentRaceResultRunners;
